
import { firestore } from "@/firebase";
import { ROOT_ACTIONS } from "@/store/actions";
import { USER_GETTERS } from "@/store/user/getters";
import { format } from "date-fns";
import { fr, nlBE } from "date-fns/locale";
import {
  collection,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  doc,
  Timestamp,
  addDoc,
} from "firebase/firestore";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component
export default class ProgramWebinarsCard extends Vue {
  @Prop({ required: true }) userId!: string;

  open = false;
  webinars = [] as any[];

  registeredWebinars = [] as any[];
  registerForWebinarDialog = false;
  selectedWebinar = "";

  get user() {
    return this.$store.getters[USER_GETTERS.ACTING_USER];
  }

  @Watch("registerForWebinarDialog")
  async registerForWebinarDialogChanged() {
    if (this.registerForWebinarDialog && this.webinars.length === 0) {
      await this.fetchWebinars();
    }
  }

  async fetchWebinars() {
    let fetchedEvents = await getDocs(
      query(
        collection(firestore, "webinars"),
        where("start", ">=", new Date()),
        orderBy("start")
      )
    );
    let events = fetchedEvents.docs.map((e) => {
      return {
        ...e.data(),
        id: e.id,
        title: `${e.data().title} - ${format(
          (e.data().start as Timestamp).toDate(),
          "dd MMMM yyyy HH:mm",
          {
            locale: this.user.language === "nl-be" ? nlBE : fr,
          }
        )} - ${e.data().type || ''}`,
        start: (e.data().start as Timestamp).toDate(),
        end: (e.data().end as Timestamp).toDate(),
      };
    });
    
    const registeredEvents = await getDocs(
      query(
        collection(firestore, "event-attendees"),
        where("user", "==", this.userId)
      )
    );
    
    // Get list of registered webinar IDs
    const registeredWebinarIds = registeredEvents.docs.map(doc => doc.data().webinarId);
    
    // Mark webinars as disabled if already registered
    this.webinars = events.map(webinar => ({
      ...webinar,
      disabled: registeredWebinarIds.includes(webinar.id)
    }));
    
    // create a list with name + teams url in registeredWebinars
    this.registeredWebinars = [];
    for (const registeredEvent of registeredEvents.docs) {
      // get the webinar
      const webinar = await getDoc(
        doc(firestore, "webinars", registeredEvent.data().webinarId)
      );
      if (!webinar.exists()) continue;

      let coachName = '—';
      const coachId = registeredEvent.data().coach;
      if (coachId) {
        const coach = await getDoc(doc(firestore, "users", coachId));
        if (coach.exists()) {
          coachName = `${coach.data().firstname} ${coach.data().lastname}`;
        }
      }

      this.registeredWebinars.push({
        title: webinar?.data().title,
        ...registeredEvent.data(),
        coach: coachName,
        url: webinar.data().teamsUrl,
        registeredByCoach: registeredEvent.data().registeredByCoach ?? false,
      });
    }
  }

  async mounted() {
    await this.fetchWebinars();
  }


  async registerForWebinar() {
    if (!this.selectedWebinar) return;

    // Check if user is already registered for this webinar
    const existingRegistration = await getDocs(
      query(
        collection(firestore, "event-attendees"),
        where("webinarId", "==", this.selectedWebinar),
        where("user", "==", this.userId)
      )
    );
    
    if (!existingRegistration.empty) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: this.$t("participantAlreadyRegistered"),
      });
      this.registerForWebinarDialog = false;
      return;
    }

    let coach = "";

    if (this.user.type === "ADMIN") {
      const programsOfUser = await getDocs(
        query(
          collection(firestore, "programs"),
          where("participant", "==", this.userId)
        )
      );
      if (!programsOfUser.empty) {
        coach = programsOfUser.docs[0].data().coach as string;
      } else {
        coach = this.user.id;
      }
    } else {
      coach = this.user.id;
    }

    await addDoc(collection(firestore, "event-attendees"), {
      webinarId: this.selectedWebinar,
      user: this.userId,
      coach,
      present: false,
      addedActivity: false,
      registeredByCoach: true,
    });

    await addDoc(collection(firestore, "webinar-invite-test"), {
      user: this.userId,
      webinar: this.selectedWebinar,
    });

    this.registerForWebinarDialog = false;
    this.$emit("refresh");
  }
}
